import { useState } from 'react';
import { FIVE_KIRIN_CONFIG, FOUR_GODS_SHARED_IMAGES } from '../../constants/fourGodsConfig';
import useVersionCheck from '../../hooks/useVersionCheck';

/**
 * FiveKirinPage - 四神獣4問まとめページ（5kirin5）
 *
 * 4神獣（ゲンブ・ビャッコ・スザク・セイリュウ）の問題を1ページに集約し、
 * 4問すべて正解した場合のみ pushPageUrl へ遷移する。
 *
 * レイアウト（画面上から順）:
 *   1. タイトルロゴ（上部中央）
 *   2. 4問分の「キャラクター画像＋テキスト＋キーワード入力欄」（縦に並ぶ）
 *   3. 送信ボタン（共通・1つ）
 *   4. エラーメッセージ（任意表示・共通1つ）
 *   5. リニューアルロゴ ＋ ©RURAL ©KFP（画面下部）
 */
const FiveKirinPage = () => {
  const { questions, backgroundImage, errorMessage, pushPageUrl } = FIVE_KIRIN_CONFIG;

  useVersionCheck();

  const [answers, setAnswers] = useState(() => questions.map(() => ''));
  const [showError, setShowError] = useState(false);

  const handleChange = (index) => (e) => {
    const next = [...answers];
    next[index] = e.target.value;
    setAnswers(next);
    if (showError) setShowError(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const allCorrect = questions.every(
      (q, i) => answers[i].trim() === q.keyword
    );

    if (allCorrect) {
      window.location.href = pushPageUrl;
    } else {
      setShowError(true);
    }
  };

  return (
    <div className="relative flex flex-col items-center w-full min-h-[100dvh]">
      {/* 背景画像（薄く表示するため opacity を下げる） */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: `url(${backgroundImage})` }}
        aria-hidden="true"
      />

      <div className="flex flex-col items-center w-full max-w-md px-6 py-4">
        {/* ① タイトルロゴ */}
        <img
          src={FOUR_GODS_SHARED_IMAGES.title}
          alt="けもフレ2026 新宿イベント"
          className="w-full max-w-[400px] object-contain shrink-0"
        />

        {/* ② 4問分の質問（キャラ画像＋テキスト＋入力欄） */}
        <form onSubmit={handleSubmit} className="flex flex-col w-full mt-4 gap-4">
          {questions.map((q, i) => (
            <div key={q.characterId} className="flex items-center gap-3 w-full">
              <img
                src={q.characterImage}
                alt=""
                className="w-16 h-16 object-contain shrink-0"
              />
              <div className="flex flex-col flex-1 gap-1">
                <p className="text-xs leading-snug">{q.text}</p>
                <input
                  type="text"
                  value={answers[i]}
                  onChange={handleChange(i)}
                  className="w-full h-10 px-3 border border-gray-800 rounded-sm bg-white text-center focus:outline-none"
                  placeholder="ひみつのことば"
                />
              </div>
            </div>
          ))}

          {/* ③ 送信ボタン（共通・1つ） */}
          <button
            type="submit"
            className="self-center px-8 py-2 mt-2 border border-gray-800 rounded-sm bg-white text-sm active:bg-gray-100"
          >
            送信
          </button>
        </form>

        {/* ④ エラーメッセージ（共通・1つ） */}
        {showError && (
          <p className="mt-2 text-center text-sm text-red-600">
            {errorMessage}
          </p>
        )}

        {/* ⑤ リニューアルロゴ ＋ ©表記（画面下部） */}
        <div className="flex flex-col items-center gap-1 mt-6">
          <img
            src={FOUR_GODS_SHARED_IMAGES.logo}
            alt="けものコレクション DIARY"
            className="max-h-[8dvh] w-auto object-contain"
          />
          <p className="text-center text-xs text-gray-600">©RURAL ©KFP</p>
        </div>
      </div>
    </div>
  );
};

export default FiveKirinPage;
